import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

export default function RefundPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600 text-white py-6">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            Refund & Cancellation Policy
          </h1>
          <p className="text-white/80 mt-2">Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-pink max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Overview</h2>
            <p className="text-gray-600 leading-relaxed">
              At MUAS Academy, we strive to provide quality makeup and beauty training. We understand that circumstances may change, and you may need to cancel or request a refund. This policy outlines the terms and conditions for refunds and cancellations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Refund Eligibility</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-2">Full Refund (100%)</h3>
            <p className="text-gray-600 leading-relaxed">
              A full refund of the course fee (excluding registration fee) is available if:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-2">
              <li>Cancellation request is made at least 15 days before the course start date</li>
              <li>MUAS Academy cancels the batch due to insufficient enrollment or unforeseen circumstances</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-2">Partial Refund (50%)</h3>
            <p className="text-gray-600 leading-relaxed">
              A 50% refund of the course fee (excluding registration fee) is available if:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-2">
              <li>Cancellation request is made 7-14 days before the course start date</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-2">No Refund</h3>
            <p className="text-gray-600 leading-relaxed">
              No refund will be provided if:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-2">
              <li>Cancellation request is made less than 7 days before the course start date</li>
              <li>The student has attended more than 20% of the course classes</li>
              <li>The student is dismissed for violating academy policies</li>
              <li>The student fails to attend classes without prior notice for more than 2 consecutive weeks</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Non-Refundable Items</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              The following fees and items are non-refundable under any circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Registration Fee:</strong> The one-time registration fee paid at the time of enrollment</li>
              <li><strong>Makeup Kits & Materials:</strong> Any kits, products, or materials already issued to the student</li>
              <li><strong>Books & Study Materials:</strong> Course books, manuals, or digital materials provided</li>
              <li><strong>Certificate Fee:</strong> Fees paid for certification processing</li>
              <li><strong>Workshop Fees:</strong> Fees for special workshops or masterclasses already attended</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Batch Cancellation by MUAS Academy</h2>
            <p className="text-gray-600 leading-relaxed">
              If MUAS Academy cancels a batch for any reason:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-2">
              <li>Students will be offered transfer to the next available batch at no additional cost</li>
              <li>Alternatively, a full refund (including registration fee) will be processed within 15 business days</li>
              <li>Students will be notified at least 7 days in advance of any batch cancellation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Rescheduling and Transfer</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Instead of cancellation, students may request to reschedule or transfer:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Batch Transfer:</strong> Transfer to a different batch (same course) is allowed once, subject to availability, with at least 7 days notice</li>
              <li><strong>Course Change:</strong> Transfer to a different course may be allowed with fee adjustment, subject to approval</li>
              <li><strong>Postponement:</strong> Students may postpone their enrollment to a future batch (within 6 months) with valid reasons, one time only</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              A rescheduling fee of ₹500 may apply for transfers made less than 7 days before the original course start date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">6. How to Request a Refund</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              To request a refund or cancellation:
            </p>
            <ol className="list-decimal pl-6 text-gray-600 space-y-2">
              <li>Submit a written request via email to <strong>muasacademy.ranchi@gmail.com</strong> or in person at the academy</li>
              <li>Include your full name, enrolled course, batch details, and reason for cancellation</li>
              <li>Attach a copy of your payment receipt</li>
              <li>Provide your bank account details for refund processing (Account holder name, Account number, IFSC code)</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Refund Processing</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Once a refund request is approved:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Refunds will be processed within <strong>7-10 business days</strong> from the date of approval</li>
              <li>Refunds will be made to the original payment method or the bank account provided</li>
              <li>For payments made via cash, refund will be via bank transfer only</li>
              <li>Any applicable bank charges or transaction fees will be deducted from the refund amount</li>
              <li>You will receive a confirmation email once the refund is processed</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Special Circumstances</h2>
            <p className="text-gray-600 leading-relaxed">
              In exceptional circumstances such as medical emergencies, family emergencies, or other genuine hardships, MUAS Academy may consider refund requests on a case-by-case basis. Supporting documentation will be required. The academy's decision in such matters will be final.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Contact for Refund Queries</h2>
            <p className="text-gray-600 leading-relaxed">
              For any questions about this Refund & Cancellation Policy or to submit a refund request, please contact:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700"><strong>MUAS Academy - Accounts Department</strong></p>
              <p className="text-gray-600">C-127, Harmu Housing Colony, Harmu Ranchi 834002</p>
              <p className="text-gray-600">Jharkhand, India</p>
              <p className="text-gray-600">Phone: +91 6201542274, +91 9773682082</p>
              <p className="text-gray-600">Email: muasacademy.ranchi@gmail.com</p>
              <p className="text-gray-600 mt-2"><strong>Business Hours:</strong> Monday - Saturday, 10:00 AM - 7:00 PM</p>
            </div>
          </section>

          <section className="mb-8 bg-pink-50 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Reference: Refund Summary</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-gray-700">Cancellation Timeline</th>
                  <th className="text-left py-2 text-gray-700">Refund Amount</th>
                </tr>
              </thead>
              <tbody className="text-gray-600">
                <tr className="border-b">
                  <td className="py-2">15+ days before course start</td>
                  <td className="py-2">100% (excl. registration fee)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">7-14 days before course start</td>
                  <td className="py-2">50% (excl. registration fee)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Less than 7 days before start</td>
                  <td className="py-2">No refund</td>
                </tr>
                <tr>
                  <td className="py-2">After 20% course completion</td>
                  <td className="py-2">No refund</td>
                </tr>
              </tbody>
            </table>
          </section>

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
